// A small structural validator.
//
// Written rather than installed, for two reasons that are specific rather than
// reflexive. The schema package is imported by the capture client, which ships
// inside a Capacitor bundle where every kilobyte is a kilobyte on a tablet in a
// locked bay, and it is imported by the ingest path, where the validator is the
// thing standing between untrusted device output and an append-only table that
// cannot be corrected afterwards. A dependency there is a supply-chain surface
// on the one code path where a bad write is permanent.
//
// It covers what the event schema actually uses and nothing else: no unions
// beyond a closed enum, no recursion, no transforms, no coercion. Coercion in
// particular is deliberately absent. A device sending the string "3" where an
// integer is required is a device with a bug, and quietly turning it into 3
// hides that bug until it matters.

export type Issue = {
  /** Dotted path from the root of the value being checked. */
  path: string;
  message: string;
};

export type Validator<T> = {
  readonly describe: string;
  check(value: unknown, path: string): Issue[];
  /** Present only so T is used. Never populated at runtime. */
  readonly _type?: T;
};

const issue = (path: string, message: string): Issue[] => [{ path, message }];

function make<T>(describe: string, check: (value: unknown, path: string) => Issue[]): Validator<T> {
  return { describe, check };
}

export const string = () =>
  make<string>("string", (v, p) =>
    typeof v === "string" ? [] : issue(p, `expected a string, got ${typeName(v)}`),
  );

export const boolean = () =>
  make<boolean>("boolean", (v, p) =>
    typeof v === "boolean" ? [] : issue(p, `expected a boolean, got ${typeName(v)}`),
  );

export const number = () =>
  make<number>("number", (v, p) =>
    typeof v === "number" && Number.isFinite(v)
      ? []
      : issue(p, `expected a finite number, got ${typeName(v)}`),
  );

export const integer = () =>
  make<number>("integer", (v, p) =>
    typeof v === "number" && Number.isInteger(v)
      ? []
      : issue(p, `expected an integer, got ${typeName(v)}`),
  );

// Version 4 specifically, per section 3: attendee_ref is a v4 UUID generated on
// the device. Accepting any UUID shape would accept a v1, which encodes a MAC
// address and a timestamp and is therefore not the meaningless identifier the
// design depends on.
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const uuid = () =>
  make<string>("uuid", (v, p) => {
    if (typeof v !== "string") return issue(p, `expected a uuid string, got ${typeName(v)}`);
    return UUID_V4.test(v) ? [] : issue(p, `expected a version 4 uuid, got ${JSON.stringify(v)}`);
  });

export const timestamp = () =>
  make<string>("timestamp", (v, p) => {
    if (typeof v !== "string") return issue(p, `expected an ISO timestamp, got ${typeName(v)}`);
    return Number.isNaN(Date.parse(v))
      ? issue(p, `expected an ISO timestamp, got ${JSON.stringify(v)}`)
      : [];
  });

/** A closed list. Anything outside it is rejected, never silently mapped. */
export const oneOf = <const V extends readonly string[]>(values: V) =>
  make<V[number]>(values.join(" | "), (v, p) =>
    typeof v === "string" && (values as readonly string[]).includes(v)
      ? []
      : issue(p, `expected one of ${values.join(", ")}, got ${JSON.stringify(v)}`),
  );

/**
 * A closed list of numbers. Same rule as oneOf.
 *
 * Exists for protocol version negotiation, where `literal` was the wrong tool:
 * pinning a version to one constant means a device on the older of two
 * supported versions cannot form a request the contract accepts, which
 * contradicts A9's "two versions supported at once".
 */
export const oneOfNumber = <const V extends readonly number[]>(values: V) =>
  make<V[number]>(values.join(" | "), (v, p) =>
    typeof v === "number" && (values as readonly number[]).includes(v)
      ? []
      : issue(p, `expected one of ${values.join(", ")}, got ${JSON.stringify(v)}`),
  );

/** Exactly one value. Used where the schema pins a field to a constant. */
export const literal = <const T extends string | number | boolean>(value: T) =>
  make<T>(JSON.stringify(value), (v, p) =>
    v === value ? [] : issue(p, `expected ${JSON.stringify(value)}, got ${JSON.stringify(v)}`),
  );

export const array = <T>(item: Validator<T>) =>
  make<T[]>(`${item.describe}[]`, (v, p) => {
    if (!Array.isArray(v)) return issue(p, `expected an array, got ${typeName(v)}`);
    return v.flatMap((entry, i) => item.check(entry, `${p}[${i}]`));
  });

/** Any JSON object. Used where the schema says "typed value" and the type is decided elsewhere. */
export const record = () =>
  make<Record<string, unknown>>("object", (v, p) =>
    isPlainObject(v) ? [] : issue(p, `expected an object, got ${typeName(v)}`),
  );

export const nullable = <T>(inner: Validator<T>) =>
  make<T | null>(`${inner.describe} | null`, (v, p) =>
    v === null ? [] : inner.check(v, p),
  );

const OPTIONAL = Symbol("optional");

export type Optional<T> = Validator<T | undefined> & { [OPTIONAL]: true };

/** A key that may be absent. Distinct from nullable: absent and null differ. */
export const optional = <T>(inner: Validator<T>): Optional<T> =>
  Object.assign(
    make<T | undefined>(`${inner.describe}?`, (v, p) =>
      v === undefined ? [] : inner.check(v, p),
    ),
    { [OPTIONAL]: true as const },
  );

export type Shape = Record<string, Validator<unknown>>;

/**
 * A closed object.
 *
 * Unknown keys are rejected rather than stripped. On the ingest path a key the
 * server does not recognise means the device is running something the server is
 * not, and the right response is to say so loudly rather than to drop it: a
 * silently stripped field is data loss that nothing reports.
 */
export const object = <S extends Shape>(shape: S) =>
  make<{ [K in keyof S]: S[K] extends Validator<infer T> ? T : never }>(
    "object",
    (v, p) => {
      if (!isPlainObject(v)) return issue(p, `expected an object, got ${typeName(v)}`);

      const issues: Issue[] = [];
      for (const [key, validator] of Object.entries(shape)) {
        const at = p ? `${p}.${key}` : key;
        const present = Object.prototype.hasOwnProperty.call(v, key);
        if (!present) {
          if (!(OPTIONAL in validator)) {
            issues.push({ path: at, message: `required key is missing` });
          }
          continue;
        }
        issues.push(...validator.check(v[key], at));
      }

      for (const key of Object.keys(v)) {
        if (!Object.prototype.hasOwnProperty.call(shape, key)) {
          const at = p ? `${p}.${key}` : key;
          issues.push({ path: at, message: `unknown key, and unknown keys are rejected rather than dropped` });
        }
      }

      return issues;
    },
  );

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function typeName(v: unknown): string {
  if (v === null) return "null";
  if (Array.isArray(v)) return "an array";
  if (v === undefined) return "nothing";
  return `a ${typeof v}`;
}
